import React, { useEffect, useRef, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import { useHistory } from 'react-router'

import useTypedDispatch from 'hooks/useDispatch'
import useSearchParams from 'hooks/useSearchParams'
import {
  useGetBookingInvitationQuery,
  useJoinTravelCrewMutation,
} from 'hooks/useQueries'
import useLoginModalState from 'hooks/useLoginModalState'

import { setReferredBy } from 'actions/authActions'

import Button from 'components/display/Button'
import TrayModal from 'components/modals/TrayModal'
import InlineSpinner from 'components/display/InlineSpinner'
import LazyImage from 'components/display/LazyImage'
import MediaWrapper from 'components/cards/shared/MediaWrapper'
import withRenderClientsideOnly from 'components/utils/withRenderClientsideOnly'
import {
  HeadingSm,
  ParagraphXs,
  ParagraphXxs,
} from 'components/display/Typography'

export const tokenKey = 'booking_invitation_token' as const

type UseAcceptAfterAuthenticatedOptions = {
  acceptInvitation: () => any
  referredById?: string
  referralType?: string
}

const useAcceptAfterAuthenticated = ({
  acceptInvitation,
  referredById,
}: UseAcceptAfterAuthenticatedOptions) => {
  const dispatch = useTypedDispatch()
  const { currentUserSignedIn, openLoginModal } = useLoginModalState()
  const userHasAccepted = useRef(false)

  useEffect(() => {
    if (userHasAccepted.current && currentUserSignedIn) {
      acceptInvitation()
    }
  }, [userHasAccepted.current, currentUserSignedIn])

  const acceptAfterAuthenticated = () => {
    userHasAccepted.current = true
    dispatch(setReferredBy(referredById, 'travel_crew'))
    openLoginModal()
  }

  return {
    isAuthenticated: currentUserSignedIn,
    acceptAfterAuthenticated,
  }
}

const JoinBooking = () => {
  const history = useHistory()
  const { addToast } = useToasts()
  const [open, setOpen] = useState(false)

  const searchParams = useSearchParams()

  const token = searchParams?.get(tokenKey)

  useEffect(() => {
    if (token) {
      setOpen(true)
    } else {
      setOpen(false)
    }
  }, [token])

  const { data, loading } = useGetBookingInvitationQuery({
    variables: { token },
    skip: !token, // The query will be skipped if token is not present
  })

  const bookingId = data?.bookingInvitation?.id

  const referredById = data?.bookingInvitation?.bookingCustomerId

  const [
    joinTravelCrew,
    { loading: joinTravelCrewLoading },
  ] = useJoinTravelCrewMutation({
    variables: {
      bookingId,
      hashid: token,
    },
    onCompleted: () => {
      addToast('Escape joined')
      setOpen(false)
      history.push(`/my/bookings/${bookingId}/itinerary`)
    },
    onError: (error) => {
      addToast(error?.message, { appearance: 'error' })
    },
  })

  const {
    isAuthenticated,
    acceptAfterAuthenticated,
  } = useAcceptAfterAuthenticated({
    acceptInvitation: joinTravelCrew,
    referredById,
  })

  const handleAccept = () => {
    if (isAuthenticated) {
      joinTravelCrew()
    } else {
      acceptAfterAuthenticated()
      setOpen(false)
    }
  }

  const handleDecline = () => {
    setOpen(false)
  }

  return (
    <TrayModal
      onRequestClose={() => setOpen(false)}
      isOpen={open}
      title="You've been invited to join this escape"
    >
      <div data-testid="content" style={{ paddingBottom: '16px' }}>
        <ParagraphXs weight="400" style={{ marginTop: '16px' }}>
          Join and contribute to the itinerary by accepting and adding new
          adventures.
        </ParagraphXs>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            margin: '16px 0',
            gap: '16px',
          }}
        >
          <div style={{ width: '40%' }}>
            <MediaWrapper>
              <LazyImage
                data-src={data?.bookingInvitation?.promoImage?.thumb}
                style={{ position: 'absolute', top: 0 }}
              />
            </MediaWrapper>
          </div>

          <div>
            <HeadingSm style={{ marginBottom: '8px' }}>
              {data?.bookingInvitation?.listing?.title}
            </HeadingSm>
            <ParagraphXxs>
              {data?.bookingInvitation?.listing?.location?.name}
            </ParagraphXxs>
          </div>
        </div>

        <div
          data-testid="buttons-wrapper"
          style={{ display: 'flex', gap: '16px' }}
        >
          <Button
            onClick={handleDecline}
            uppercase={false}
            ghost
            disabled={joinTravelCrewLoading}
            style={{ flexGrow: 1, width: '50%' }}
          >
            Decline
          </Button>
          <Button
            onClick={handleAccept}
            uppercase={false}
            disabled={loading || joinTravelCrewLoading}
            style={{ flexGrow: 1, width: '50%' }}
          >
            {joinTravelCrewLoading ? <InlineSpinner /> : 'Accept'}
          </Button>
        </div>
      </div>
    </TrayModal>
  )
}

export default withRenderClientsideOnly(JoinBooking)
